import React, { FC } from 'react';
import { Product } from 'shared/types/product/Product';
import { SwiperOptions } from 'swiper';
import Slider from 'components/commercetools-ui/atoms/slider';
import Subtitle, { SubtitleProps } from 'components/commercetools-ui/atoms/subtitle';
import Typography from 'components/commercetools-ui/atoms/typography';
import Wrapper from 'components/HOC/wrapper';
import useClassNames from 'helpers/hooks/useClassNames';
import useTouchDevice from 'helpers/hooks/useTouchDevice';
import { desktop, mediumDesktop, minitablet, mobile, smallMobile, tablet } from 'helpers/utils/screensizes';
import { Reference } from 'types/reference';
import useTrack from './useTrack';
import Link from '../../content/link';
import Tile from '../product-tile';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { usePathname, useRouter } from 'next/navigation';
import { isPdpUrl } from 'helpers/utils/pdpUrlHelper';

export interface ProductSliderProps extends Partial<SwiperOptions> {
  products: Product[];
  title: string;
  titleVariant?: 'xs' | 'sm' | 'lg';
  subline?: string;
  subtitleVariant?: SubtitleProps['variant'];
  ctaLabel?: string;
  ctaLink?: Reference;
  clearDefaultWrapperStyles?: boolean;
  slidesPerView?: number;
  disableProductQuickView?: boolean;
  disableProductWishlistButton?: boolean;
  disableProductVariants?: boolean;
  innerArrows?: boolean;
  solidArrows?: boolean;
  onProductClick?: (product: Product) => void;
  classNames?: {
    title?: string;
  };
  variant?: 'normal' | 'cart';
}



const ProductSlider: FC<ProductSliderProps> = ({
  products,
  title,
  subline,
  ctaLabel,
  ctaLink,
  // Not forwarded to the tile (it never used them); destructured so they stay out of `...props` → <Slider>.
  /* eslint-disable @typescript-eslint/no-unused-vars */
  onProductClick,
  titleVariant = 'lg',
  subtitleVariant = 'lg',
  clearDefaultWrapperStyles = false,
  disableProductQuickView = false,
  disableProductWishlistButton = false,
  disableProductVariants = false,
  /* eslint-enable @typescript-eslint/no-unused-vars */
  innerArrows = true,
  solidArrows = true,
  breakpoints = {},
  classNames = {},
  ...props
}) => {

  const { isTouchDevice } = useTouchDevice();

  
  const [isMiniTabletSize] = useMediaQuery(minitablet);
  const [isTabletSize] = useMediaQuery(tablet);
  const [isMobileSize] = useMediaQuery(mobile);
  const [isDesktopSize] = useMediaQuery(desktop);
  const [isMediumDesktopSize] = useMediaQuery(mediumDesktop);
  const titleClassName = useClassNames([
    'mb-12 text-20',
    classNames.title,
    {
      xs: 'md:text-32',
      sm: 'md:text-18 lg:text-32',
      lg: 'md:text-22 lg:text-42',
    }[titleVariant ?? 'lg'],
  ]);
  const currentURL = usePathname();
  const isProductDetailPage = isPdpUrl(currentURL);
  return (
    <div className={`pt-22 md:pt-22 pb-48 md:pb-48 lg:py-48 xl:py-70 2xl:py-70 3xl:py-103 xl:pr-0  ${isProductDetailPage?' lg:grid lg:grid-cols-12 pr-0  md:pb-1  bg-[#F3EDE9] w-screen relative left-full -translate-x-full':"md:px-40"}`} >
      <div  id='related-products' className={isProductDetailPage?'lg:col-span-9 lg:col-start-4 pt-26 pl-24 md:pl-28 lg:pl-30 xl:pl-0 2xl:pl-0 xl:py-60':'pl-24'}>
      <div  >
        <Typography id='related-products-title' className={`text-32 text-primary-black pb-32 xl:text-36 2xl:text-48 font-medium  ${isProductDetailPage?'':""} `} as="h3">
          {title}
        </Typography>
        {/* {(subline || ctaLink) && (
          <div className="mt-8 flex items-center justify-between md:mt-16 lg:mt-14">
            {subline && <Subtitle subtitle={subline} variant={subtitleVariant} />}
            {ctaLink && (
              <div className="hidden lg:block">
                <Link target={ctaLink} withArrow>
                  <span className="font-medium leading-[24px] text-secondary-black">{ctaLabel}</span>
                </Link>
              </div>
            )}
          </div>
        )}
        {ctaLink && (
          <div className="mt-20 block md:mt-16 lg:hidden">
            <Link target={ctaLink} withArrow>
              <span className="font-medium leading-[24px] text-secondary-black">{ctaLabel}</span>
            </Link>
          </div>
        )} */}
      </div>
      <div className={isProductDetailPage?'':''}>
        <div className="product-slider-container relative w-full">
          <Slider className='mb-48'
            slidesPerView={2.1}
            slidesPerGroup={1}
            dots={false}
           arrows = {isTabletSize}
            innerArrows={innerArrows}
            solidArrows={solidArrows}
            nextButtonStyles={{top:'110%', left:50, padding: '10px'}}
            prevButtonStyles={{top:'110%', left:0, padding: '5px'}}
            allowTouchMove
            allowArrowsOnTouchDevice

            spaceBetween={8}
            breakpoints={{
              [tablet]: {
                slidesPerView: isProductDetailPage?3.25: 4 ,
                slidesPerGroup:  3,
                spaceBetween: 16,
                ...(breakpoints[tablet] ?? {}),
              },
              [smallMobile]: {
                slidesPerView: isProductDetailPage?1.2: 1.2,
                slidesPerGroup:  1,
                spaceBetween: 16,
                ...(breakpoints[smallMobile] ?? {}),
              },
              [mobile]: {
                slidesPerView: isProductDetailPage?2.1: 2.1,
                slidesPerGroup:  2,
                spaceBetween: 16,
                ...(breakpoints[mobile] ?? {}),
              },
              [mediumDesktop]: {
                slidesPerView: isProductDetailPage?3.25: 3.5 ,
                slidesPerGroup:  3,
                spaceBetween: 16,
                ...(breakpoints[mediumDesktop] ?? {}),
              }

            }}
            {...props}
          >
            {products.map((product, index) => (
              <Tile key={product.productId} product={product} layout="grid" />
            ))}
          </Slider>
        </div>
      </div>
      </div>
    </div>
  );
};

export default ProductSlider;